import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchLibraryFolderTree,
  fetchLibraryFolders,
  createLibraryFolder,
  updateLibraryFolder,
  deleteLibraryFolder,
  moveLibraryFolder,
  reorderLibraryFolders,
  fetchLibraryScenariosInFolder,
  fetchAllLibraryScenarios,
  createLibraryScenario,
  updateLibraryScenario,
  deleteLibraryScenario,
  moveLibraryScenarioToFolder,
  reorderLibraryScenarios,
  cloneLibraryScenario,
  copyLibraryFolderToRelease,
  copyLibraryScenarioToRelease,
  bulkCopyLibraryScenariosToRelease
} from '../../api/libraryApi'

// ============================================================================
// ASYNC THUNKS - LIBRARY FOLDERS
// ============================================================================

/**
 * FETCH LIBRARY FOLDER TREE
 * Fetches hierarchical folder tree for the library (project-level)
 */
export const fetchLibraryFolderTreeAsync = createAsyncThunk(
  'library/fetchFolderTree',
  async (jiraProjectId, { rejectWithValue }) => {
    try {
      return await fetchLibraryFolderTree(jiraProjectId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch library folder tree',
        status: error.response?.status
      })
    }
  }
)

/**
 * FETCH LIBRARY FOLDERS (flat list)
 */
export const fetchLibraryFoldersAsync = createAsyncThunk(
  'library/fetchFolders',
  async (jiraProjectId, { rejectWithValue }) => {
    try {
      return await fetchLibraryFolders(jiraProjectId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch library folders',
        status: error.response?.status
      })
    }
  }
)

/**
 * CREATE LIBRARY FOLDER
 */
export const createLibraryFolderAsync = createAsyncThunk(
  'library/createFolder',
  async (folderData, { rejectWithValue }) => {
    try {
      return await createLibraryFolder(folderData)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to create library folder',
        status: error.response?.status
      })
    }
  }
)

/**
 * UPDATE LIBRARY FOLDER
 */
export const updateLibraryFolderAsync = createAsyncThunk(
  'library/updateFolder',
  async ({ folderId, updates }, { rejectWithValue }) => {
    try {
      const data = await updateLibraryFolder(folderId, updates)
      return { folderId, data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to update library folder',
        status: error.response?.status
      })
    }
  }
)

/**
 * DELETE LIBRARY FOLDER
 */
export const deleteLibraryFolderAsync = createAsyncThunk(
  'library/deleteFolder',
  async (folderId, { rejectWithValue }) => {
    try {
      await deleteLibraryFolder(folderId)
      return { folderId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to delete library folder',
        status: error.response?.status
      })
    }
  }
)

/**
 * MOVE LIBRARY FOLDER
 */
export const moveLibraryFolderAsync = createAsyncThunk(
  'library/moveFolder',
  async ({ folderId, moveData }, { rejectWithValue }) => {
    try {
      const data = await moveLibraryFolder(folderId, moveData)
      return { folderId, data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to move library folder',
        status: error.response?.status
      })
    }
  }
)

/**
 * REORDER LIBRARY FOLDERS
 */
export const reorderLibraryFoldersAsync = createAsyncThunk(
  'library/reorderFolders',
  async (reorderData, { rejectWithValue }) => {
    try {
      await reorderLibraryFolders(reorderData)
      return reorderData
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to reorder library folders',
        status: error.response?.status
      })
    }
  }
)

// ============================================================================
// ASYNC THUNKS - LIBRARY SCENARIOS
// ============================================================================

/**
 * FETCH LIBRARY SCENARIOS IN FOLDER
 */
export const fetchLibraryScenariosInFolderAsync = createAsyncThunk(
  'library/fetchScenariosInFolder',
  async (folderId, { rejectWithValue }) => {
    try {
      const data = await fetchLibraryScenariosInFolder(folderId)
      return { folderId, scenarios: data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch library scenarios',
        status: error.response?.status
      })
    }
  }
)

/**
 * FETCH ALL LIBRARY SCENARIOS
 */
export const fetchAllLibraryScenariosAsync = createAsyncThunk(
  'library/fetchAllScenarios',
  async (jiraProjectId, { rejectWithValue }) => {
    try {
      return await fetchAllLibraryScenarios(jiraProjectId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch all library scenarios',
        status: error.response?.status
      })
    }
  }
)

/**
 * CREATE LIBRARY SCENARIO
 */
export const createLibraryScenarioAsync = createAsyncThunk(
  'library/createScenario',
  async (scenarioData, { rejectWithValue }) => {
    try {
      return await createLibraryScenario(scenarioData)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to create library scenario',
        status: error.response?.status
      })
    }
  }
)

/**
 * UPDATE LIBRARY SCENARIO
 */
export const updateLibraryScenarioAsync = createAsyncThunk(
  'library/updateScenario',
  async ({ scenarioId, updates }, { rejectWithValue }) => {
    try {
      const data = await updateLibraryScenario(scenarioId, updates)
      return { scenarioId, data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to update library scenario',
        status: error.response?.status
      })
    }
  }
)

/**
 * DELETE LIBRARY SCENARIO
 */
export const deleteLibraryScenarioAsync = createAsyncThunk(
  'library/deleteScenario',
  async (scenarioId, { rejectWithValue }) => {
    try {
      await deleteLibraryScenario(scenarioId)
      return { scenarioId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to delete library scenario',
        status: error.response?.status
      })
    }
  }
)

/**
 * MOVE LIBRARY SCENARIO TO FOLDER
 */
export const moveLibraryScenarioToFolderAsync = createAsyncThunk(
  'library/moveScenarioToFolder',
  async ({ scenarioId, moveData }, { rejectWithValue }) => {
    try {
      const data = await moveLibraryScenarioToFolder(scenarioId, moveData)
      return { scenarioId, data }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to move library scenario',
        status: error.response?.status
      })
    }
  }
)

/**
 * REORDER LIBRARY SCENARIOS
 */
export const reorderLibraryScenariosAsync = createAsyncThunk(
  'library/reorderScenarios',
  async (reorderData, { rejectWithValue }) => {
    try {
      await reorderLibraryScenarios(reorderData)
      return reorderData
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to reorder library scenarios',
        status: error.response?.status
      })
    }
  }
)

/**
 * CLONE LIBRARY SCENARIO
 * Creates a deep copy of a library scenario in the same folder.
 * Backend duplicates all test cases, steps, attachments, and labels and
 * appends " (Copy)" to the scenario name.
 */
export const cloneLibraryScenarioAsync = createAsyncThunk(
  'library/cloneScenario',
  async (scenarioId, { rejectWithValue }) => {
    try {
      return await cloneLibraryScenario(scenarioId)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to clone library scenario',
        status: error.response?.status
      })
    }
  }
)

// ============================================================================
// ASYNC THUNKS - COPY/IMPORT OPERATIONS
// ============================================================================

/**
 * COPY LIBRARY FOLDER TO RELEASE
 * Copies an entire library folder (with all nested content) to a release
 */
export const copyLibraryFolderToReleaseAsync = createAsyncThunk(
  'library/copyFolderToRelease',
  async ({ folderId, targetReleaseId, targetParentFolderId }, { rejectWithValue }) => {
    try {
      const data = await copyLibraryFolderToRelease(folderId, {
        targetReleaseId,
        targetParentFolderId
      })
      return data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to copy library folder to release',
        status: error.response?.status
      })
    }
  }
)

/**
 * COPY LIBRARY SCENARIO TO RELEASE
 * Copies a single library scenario to a release folder
 */
export const copyLibraryScenarioToReleaseAsync = createAsyncThunk(
  'library/copyScenarioToRelease',
  async ({ scenarioId, targetReleaseId, targetFolderId }, { rejectWithValue }) => {
    try {
      const data = await copyLibraryScenarioToRelease(scenarioId, {
        targetReleaseId,
        targetFolderId
      })
      return data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to copy library scenario to release',
        status: error.response?.status
      })
    }
  }
)

/**
 * BULK COPY LIBRARY SCENARIOS TO RELEASE
 * Copies multiple library scenarios to a release folder in a single operation
 */
export const bulkCopyLibraryScenariosToReleaseAsync = createAsyncThunk(
  'library/bulkCopyToRelease',
  async ({ scenarioIds, targetReleaseId, targetFolderId }, { rejectWithValue }) => {
    try {
      const data = await bulkCopyLibraryScenariosToRelease({
        scenarioIds,
        targetReleaseId,
        targetFolderId
      })
      return data
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to copy library scenarios to release',
        status: error.response?.status
      })
    }
  }
)

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState = {
  // Hierarchical folder tree for the library
  libraryFolderTree: [],

  // Flat list of all library folders (for quick lookups)
  libraryFolders: [],

  // Currently selected folder ID (null initially, defaults to "Home" folder on load)
  selectedLibraryFolderId: null,

  // Scenarios for the selected folder
  libraryScenarios: [],

  // All scenarios in the library (for "All Library Folders" view)
  allLibraryScenarios: [],

  // Expanded folder IDs in the tree (for collapse/expand state)
  expandedLibraryFolderIds: [],

  // Loading states
  foldersLoading: false,
  scenariosLoading: false,

  // Import operation state
  importInProgress: false,
  importProgress: {
    current: 0,
    total: 0,
    status: null // 'pending', 'copying', 'completed', 'error'
  },

  // Error state
  error: null
}

// ============================================================================
// SLICE
// ============================================================================

const librarySlice = createSlice({
  name: 'library',
  initialState,
  reducers: {
    /**
     * Select a library folder (shows its scenarios in the right panel)
     */
    selectLibraryFolder: (state, action) => {
      // Only clear scenarios if selecting a DIFFERENT folder
      if (state.selectedLibraryFolderId !== action.payload) {
        state.selectedLibraryFolderId = action.payload
        // Clear scenarios when selecting a new folder (will be fetched)
        state.libraryScenarios = []
      }
    },

    /**
     * Toggle folder expand/collapse in the tree
     */
    toggleLibraryFolderExpand: (state, action) => {
      const folderId = action.payload
      if (state.expandedLibraryFolderIds.includes(folderId)) {
        state.expandedLibraryFolderIds = state.expandedLibraryFolderIds.filter(id => id !== folderId)
      } else {
        state.expandedLibraryFolderIds.push(folderId)
      }
    },

    /**
     * Set expanded folder IDs (for restoring from localStorage)
     */
    setExpandedLibraryFolderIds: (state, action) => {
      state.expandedLibraryFolderIds = action.payload
    },

    /**
     * Clear library state (when navigating away or changing projects)
     */
    clearLibrary: (state) => {
      state.libraryFolderTree = []
      state.libraryFolders = []
      state.selectedLibraryFolderId = null
      state.libraryScenarios = []
      state.allLibraryScenarios = []
      state.error = null
    },

    /**
     * Clear error state
     */
    clearLibraryError: (state) => {
      state.error = null
    },

    /**
     * Optimistically reorder scenarios (before API call)
     */
    reorderLibraryScenariosOptimistic: (state, action) => {
      const reorderData = action.payload // Array of { id, orderIndex }
      // Create a map for quick lookup
      const orderMap = new Map(reorderData.map(item => [item.id, item.orderIndex]))

      // Update orderIndex in scenarios list
      state.libraryScenarios = state.libraryScenarios.map(scenario => ({
        ...scenario,
        orderIndex: orderMap.has(scenario.id) ? orderMap.get(scenario.id) : scenario.orderIndex
      }))

      // Sort by orderIndex
      state.libraryScenarios.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0))

      // Also update in allLibraryScenarios
      state.allLibraryScenarios = state.allLibraryScenarios.map(scenario => ({
        ...scenario,
        orderIndex: orderMap.has(scenario.id) ? orderMap.get(scenario.id) : scenario.orderIndex
      }))
    },

    /**
     * Set import progress state
     */
    setImportProgress: (state, action) => {
      state.importProgress = { ...state.importProgress, ...action.payload }
    },

    /**
     * Reset import state
     */
    resetImportState: (state) => {
      state.importInProgress = false
      state.importProgress = {
        current: 0,
        total: 0,
        status: null
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ====================================================================
      // FETCH LIBRARY FOLDER TREE
      // ====================================================================
      .addCase(fetchLibraryFolderTreeAsync.pending, (state) => {
        state.foldersLoading = true
        state.error = null
      })
      .addCase(fetchLibraryFolderTreeAsync.fulfilled, (state, action) => {
        state.foldersLoading = false
        state.libraryFolderTree = action.payload || []
        // Also flatten for quick lookups
        const flatten = (folders) => {
          let result = []
          folders.forEach(folder => {
            result.push(folder)
            if (folder.children && folder.children.length > 0) {
              result = result.concat(flatten(folder.children))
            }
          })
          return result
        }
        state.libraryFolders = flatten(state.libraryFolderTree)
        state.error = null
      })
      .addCase(fetchLibraryFolderTreeAsync.rejected, (state, action) => {
        state.foldersLoading = false
        state.error = action.payload?.message || 'Failed to fetch library folders'
      })

      // ====================================================================
      // FETCH LIBRARY FOLDERS (flat)
      // ====================================================================
      .addCase(fetchLibraryFoldersAsync.fulfilled, (state, action) => {
        state.libraryFolders = action.payload || []
      })

      // ====================================================================
      // CREATE LIBRARY FOLDER
      // ====================================================================
      .addCase(createLibraryFolderAsync.fulfilled, (state, action) => {
        const newFolder = action.payload
        state.libraryFolders.push(newFolder)
        // Add to tree at appropriate location
        if (!newFolder.parentId) {
          state.libraryFolderTree.push({ ...newFolder, children: [] })
        } else {
          // Find parent and add as child (recursive function needed for deep nesting)
          const addToParent = (folders) => {
            for (let folder of folders) {
              if (folder.id === newFolder.parentId) {
                if (!folder.children) folder.children = []
                folder.children.push({ ...newFolder, children: [] })
                return true
              }
              if (folder.children && addToParent(folder.children)) {
                return true
              }
            }
            return false
          }
          addToParent(state.libraryFolderTree)
        }
      })
      .addCase(createLibraryFolderAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // UPDATE LIBRARY FOLDER
      // ====================================================================
      .addCase(updateLibraryFolderAsync.fulfilled, (state, action) => {
        const { folderId, data } = action.payload
        // Update in flat list
        const index = state.libraryFolders.findIndex(f => f.id === folderId)
        if (index !== -1) {
          state.libraryFolders[index] = { ...state.libraryFolders[index], ...data }
        }
        // Update in tree (recursive)
        const updateInTree = (folders) => {
          for (let folder of folders) {
            if (folder.id === folderId) {
              Object.assign(folder, data)
              return true
            }
            if (folder.children && updateInTree(folder.children)) {
              return true
            }
          }
          return false
        }
        updateInTree(state.libraryFolderTree)
      })
      .addCase(updateLibraryFolderAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // DELETE LIBRARY FOLDER
      // ====================================================================
      .addCase(deleteLibraryFolderAsync.fulfilled, (state, action) => {
        const { folderId } = action.payload
        // Remove from flat list
        state.libraryFolders = state.libraryFolders.filter(f => f.id !== folderId)
        // Remove from tree (recursive)
        const removeFromTree = (folders) => {
          const index = folders.findIndex(f => f.id === folderId)
          if (index !== -1) {
            folders.splice(index, 1)
            return true
          }
          for (let folder of folders) {
            if (folder.children && removeFromTree(folder.children)) {
              return true
            }
          }
          return false
        }
        removeFromTree(state.libraryFolderTree)
        // Clear selection if deleted folder was selected
        if (state.selectedLibraryFolderId === folderId) {
          state.selectedLibraryFolderId = null
          state.libraryScenarios = []
        }
      })
      .addCase(deleteLibraryFolderAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // FETCH LIBRARY SCENARIOS IN FOLDER
      // ====================================================================
      .addCase(fetchLibraryScenariosInFolderAsync.pending, (state) => {
        state.scenariosLoading = true
      })
      .addCase(fetchLibraryScenariosInFolderAsync.fulfilled, (state, action) => {
        state.scenariosLoading = false
        state.libraryScenarios = action.payload.scenarios || []
      })
      .addCase(fetchLibraryScenariosInFolderAsync.rejected, (state, action) => {
        state.scenariosLoading = false
        state.error = action.payload?.message || 'Failed to fetch library scenarios'
      })

      // ====================================================================
      // FETCH ALL LIBRARY SCENARIOS
      // ====================================================================
      .addCase(fetchAllLibraryScenariosAsync.pending, (state) => {
        state.scenariosLoading = true
      })
      .addCase(fetchAllLibraryScenariosAsync.fulfilled, (state, action) => {
        state.scenariosLoading = false
        state.allLibraryScenarios = action.payload || []
        // If "All" is selected, show these scenarios
        if (state.selectedLibraryFolderId === null) {
          state.libraryScenarios = state.allLibraryScenarios
        }
      })
      .addCase(fetchAllLibraryScenariosAsync.rejected, (state, action) => {
        state.scenariosLoading = false
        state.error = action.payload?.message || 'Failed to fetch all library scenarios'
      })

      // ====================================================================
      // CREATE LIBRARY SCENARIO
      // ====================================================================
      .addCase(createLibraryScenarioAsync.fulfilled, (state, action) => {
        const newScenario = action.payload
        // Add to scenarios list if in current folder
        if (state.selectedLibraryFolderId === newScenario.folderId ||
          (state.selectedLibraryFolderId === null && !newScenario.folderId)) {
          state.libraryScenarios.push(newScenario)
        }
        // Always add to allLibraryScenarios
        state.allLibraryScenarios.push(newScenario)
      })
      .addCase(createLibraryScenarioAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to create library scenario'
      })

      // ====================================================================
      // UPDATE LIBRARY SCENARIO
      // ====================================================================
      .addCase(updateLibraryScenarioAsync.fulfilled, (state, action) => {
        const { scenarioId, data } = action.payload
        // Update in scenarios list
        const index = state.libraryScenarios.findIndex(s => s.id === scenarioId)
        if (index !== -1) {
          state.libraryScenarios[index] = { ...state.libraryScenarios[index], ...data }
        }
        // Update in allLibraryScenarios
        const allIndex = state.allLibraryScenarios.findIndex(s => s.id === scenarioId)
        if (allIndex !== -1) {
          state.allLibraryScenarios[allIndex] = { ...state.allLibraryScenarios[allIndex], ...data }
        }
      })
      .addCase(updateLibraryScenarioAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to update library scenario'
      })

      // ====================================================================
      // DELETE LIBRARY SCENARIO
      // ====================================================================
      .addCase(deleteLibraryScenarioAsync.fulfilled, (state, action) => {
        const { scenarioId } = action.payload
        state.libraryScenarios = state.libraryScenarios.filter(s => s.id !== scenarioId)
        state.allLibraryScenarios = state.allLibraryScenarios.filter(s => s.id !== scenarioId)
      })
      .addCase(deleteLibraryScenarioAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to delete library scenario'
      })

      // ====================================================================
      // MOVE LIBRARY SCENARIO TO FOLDER
      // ====================================================================
      .addCase(moveLibraryScenarioToFolderAsync.fulfilled, (state, action) => {
        const { scenarioId, data } = action.payload
        // Update folder assignment in allLibraryScenarios
        const allIndex = state.allLibraryScenarios.findIndex(s => s.id === scenarioId)
        if (allIndex !== -1) {
          state.allLibraryScenarios[allIndex] = { ...state.allLibraryScenarios[allIndex], ...data }
        }
        // If scenario moved out of current folder, remove from scenarios
        const index = state.libraryScenarios.findIndex(s => s.id === scenarioId)
        if (index !== -1 && data.folderId !== state.selectedLibraryFolderId) {
          state.libraryScenarios.splice(index, 1)
        }
      })
      .addCase(moveLibraryScenarioToFolderAsync.rejected, (state, action) => {
        state.error = action.payload?.message || 'Failed to move library scenario'
      })

      // ====================================================================
      // CLONE LIBRARY SCENARIO
      // ====================================================================
      .addCase(cloneLibraryScenarioAsync.fulfilled, (state, action) => {
        const clonedScenario = action.payload
        // Add to scenarios list if it belongs to the currently selected folder
        // (or the "All Library Scenarios" view when no folder is selected)
        if (state.selectedLibraryFolderId === clonedScenario.folderId ||
          (state.selectedLibraryFolderId === null && !clonedScenario.folderId)) {
          state.libraryScenarios.push(clonedScenario)
        }
        // Always add to allLibraryScenarios so the "All" view stays in sync
        state.allLibraryScenarios.push(clonedScenario)
      })
      .addCase(cloneLibraryScenarioAsync.rejected, () => {
        // Error handled by component via .unwrap().catch() + showError notification
      })

      // ====================================================================
      // COPY OPERATIONS
      // ====================================================================
      .addCase(copyLibraryFolderToReleaseAsync.pending, (state) => {
        state.importInProgress = true
        state.importProgress = { current: 0, total: 1, status: 'copying' }
      })
      .addCase(copyLibraryFolderToReleaseAsync.fulfilled, (state) => {
        state.importInProgress = false
        state.importProgress = { current: 1, total: 1, status: 'completed' }
      })
      .addCase(copyLibraryFolderToReleaseAsync.rejected, (state, action) => {
        state.importInProgress = false
        state.importProgress = { current: 0, total: 0, status: 'error' }
        state.error = action.payload?.message || 'Failed to copy library folder to release'
      })

      .addCase(copyLibraryScenarioToReleaseAsync.pending, (state) => {
        state.importInProgress = true
        state.importProgress = { current: 0, total: 1, status: 'copying' }
      })
      .addCase(copyLibraryScenarioToReleaseAsync.fulfilled, (state) => {
        state.importInProgress = false
        state.importProgress = { current: 1, total: 1, status: 'completed' }
      })
      .addCase(copyLibraryScenarioToReleaseAsync.rejected, (state, action) => {
        state.importInProgress = false
        state.importProgress = { current: 0, total: 0, status: 'error' }
        state.error = action.payload?.message || 'Failed to copy library scenario to release'
      })

      .addCase(bulkCopyLibraryScenariosToReleaseAsync.pending, (state, action) => {
        state.importInProgress = true
        const total = action.meta.arg.scenarioIds?.length || 0
        state.importProgress = { current: 0, total, status: 'copying' }
      })
      .addCase(bulkCopyLibraryScenariosToReleaseAsync.fulfilled, (state, action) => {
        state.importInProgress = false
        const total = action.payload?.length || 0
        state.importProgress = { current: total, total, status: 'completed' }
      })
      .addCase(bulkCopyLibraryScenariosToReleaseAsync.rejected, (state, action) => {
        state.importInProgress = false
        state.importProgress = { current: 0, total: 0, status: 'error' }
        state.error = action.payload?.message || 'Failed to copy library scenarios to release'
      })
  }
})

// ============================================================================
// ACTIONS
// ============================================================================

export const {
  selectLibraryFolder,
  toggleLibraryFolderExpand,
  setExpandedLibraryFolderIds,
  clearLibrary,
  clearLibraryError,
  reorderLibraryScenariosOptimistic,
  setImportProgress,
  resetImportState
} = librarySlice.actions

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * Select library folder tree (hierarchical)
 */
export const selectLibraryFolderTree = (state) => state.library?.libraryFolderTree || []

/**
 * Select all library folders (flat list)
 */
export const selectLibraryFolders = (state) => state.library?.libraryFolders || []

/**
 * Select currently selected library folder ID
 */
export const selectSelectedLibraryFolderId = (state) => state.library?.selectedLibraryFolderId ?? null

/**
 * Select library scenarios for the current folder
 */
export const selectLibraryScenarios = (state) => state.library?.libraryScenarios || []

/**
 * Select all library scenarios
 */
export const selectAllLibraryScenarios = (state) => state.library?.allLibraryScenarios || []

/**
 * Select expanded library folder IDs
 */
export const selectExpandedLibraryFolderIds = (state) => state.library?.expandedLibraryFolderIds || []

/**
 * Select library folders loading state
 */
export const selectLibraryFoldersLoading = (state) => state.library?.foldersLoading || false

/**
 * Select library scenarios loading state
 */
export const selectLibraryScenariosLoading = (state) => state.library?.scenariosLoading || false

/**
 * Select library error state
 */
export const selectLibraryError = (state) => state.library?.error || null

/**
 * Select the currently selected library folder object
 */
export const selectCurrentLibraryFolder = (state) => {
  const folderId = state.library?.selectedLibraryFolderId
  if (folderId === null || folderId === undefined) return null
  return (state.library?.libraryFolders || []).find(f => f.id === folderId) || null
}

/**
 * Select import in progress state
 */
export const selectImportInProgress = (state) => state.library?.importInProgress || false

/**
 * Select import progress details
 */
export const selectImportProgress = (state) => state.library?.importProgress || { current: 0, total: 0, status: null }

export default librarySlice.reducer
